import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '../lib/supabase';
import CountdownTimer from './CountdownTimer';
import RegistrationMeter from './RegistrationMeter';

export default function RegistrationFloater() {
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(false);
  const [registrationEnabled, setRegistrationEnabled] = useState(true);
  const [registrationDate, setRegistrationDate] = useState<string>('2025-04-23T00:00:00');
  const [totalCapacity, setTotalCapacity] = useState(250);
  const [currentRegistrations, setCurrentRegistrations] = useState(0);
  const [reservedSpots, setReservedSpots] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchRegistrationData();
  }, []);

  const fetchRegistrationData = async () => {
    try {
      setIsLoading(true);

      const { data: regEnabledSetting } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'registration_enabled')
        .maybeSingle();

      if (regEnabledSetting && regEnabledSetting.value !== true && regEnabledSetting.value !== 'true') {
        setRegistrationEnabled(false);
        setIsLoading(false);
        return;
      }

      const { data: tournaments } = await supabase
        .from('tournaments')
        .select('id, capacity, registration_open_date')
        .eq('status', 'upcoming')
        .order('start_date', { ascending: true })
        .limit(1);

      if (tournaments && tournaments.length > 0) {
        setTotalCapacity(tournaments[0].capacity);

        if (tournaments[0].registration_open_date) {
          const openingDate = new Date(tournaments[0].registration_open_date);
          setRegistrationDate(tournaments[0].registration_open_date);
          setIsRegistrationOpen(new Date() >= openingDate);
        }

        const { count: playersCount } = await supabase
          .from('players')
          .select('*', { count: 'exact', head: true })
          .eq('tournament_id', tournaments[0].id);

        if (playersCount !== null) {
          setCurrentRegistrations(playersCount);
        }
      }

      const { data: reservedData } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'reserved_spots')
        .maybeSingle();

      if (reservedData?.value) {
        setReservedSpots(Number(reservedData.value));
      }
    } catch (error) {
      console.error('Error fetching registration data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const isFull = currentRegistrations + reservedSpots >= totalCapacity;

  if (isLoading || !registrationEnabled || isFull) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.6 }}
      className="absolute bottom-20 left-0 right-0 z-20 px-4"
    >
      <div className="container mx-auto max-w-2xl">
        {isRegistrationOpen ? (
          <RegistrationMeter
            totalCapacity={totalCapacity}
            currentRegistrations={currentRegistrations}
            reservedSpots={reservedSpots}
            showButton={true}
          />
        ) : (
          <CountdownTimer />
        )}
      </div>
    </motion.div>
  );
}
